import { create } from 'zustand';
import { QRGenerateRequest } from '@/types';
import { apiService } from '@/services/api.service';
import { AsyncState } from './types';

interface QRCodeData {
  vCardQR: string;
}

interface QRState extends AsyncState {
  // State - Map of cardId to QR code data
  qrCache: Map<number, QRCodeData>;
  
  // Actions
  generateQR: (cardId: number, accessToken: string, customColors?: { primary: string; secondary: string }) => Promise<QRCodeData>;
  getQR: (cardId: number) => QRCodeData | undefined;
  getCachedOrGenerate: (cardId: number, accessToken: string, qrCodeUrl?: string) => Promise<QRCodeData>;
  clearQR: (cardId: number) => void;
  clearAllQR: () => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  qrCache: new Map<number, QRCodeData>(),
  loading: false,
  error: null,
};

export const useQRStore = create<QRState>((set, get) => ({
  ...initialState,

  /**
   * Generate QR code for a card (vCard only)
   */
  generateQR: async (
    cardId: number,
    accessToken: string,
    customColors?: { primary: string; secondary: string }
  ) => {
    set({ loading: true, error: null });
    try {
      const request: QRGenerateRequest = {
        cardId,
        ...(customColors && { customColors }),
      };

      const response = await apiService.generateQRCode(request, accessToken);
      
      // Extract QR data from response
      const qrData: QRCodeData = {
        vCardQR: response.data.vCardQR || response.data.qrCode || '',
      };

      // Cache the QR code
      const { qrCache } = get();
      const newCache = new Map(qrCache);
      newCache.set(cardId, qrData);

      set({
        qrCache: newCache,
        loading: false,
      });

      return qrData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate QR code';
      set({
        error: errorMessage,
        loading: false,
      });
      throw error;
    }
  },

  /**
   * Get QR code from cache without generating
   */
  getQR: (cardId: number) => {
    const { qrCache } = get();
    return qrCache.get(cardId);
  },

  /**
   * Smart method: Get from cache if available, otherwise generate
   * Also handles cards with existing qrCodeUrl from backend
   */
  getCachedOrGenerate: async (
    cardId: number,
    accessToken: string,
    qrCodeUrl?: string
  ) => {
    // Check cache first
    const cached = get().getQR(cardId);
    if (cached) {
      return cached;
    }

    // If card has qrCodeUrl from backend, use it without generating
    if (qrCodeUrl) {
      const qrData: QRCodeData = {
        vCardQR: qrCodeUrl,
      };

      // Cache it
      const { qrCache } = get();
      const newCache = new Map(qrCache);
      newCache.set(cardId, qrData);
      set({ qrCache: newCache });

      return qrData;
    }

    // Generate new QR code
    return get().generateQR(cardId, accessToken);
  },

  /**
   * Clear QR codes for a specific card
   */
  clearQR: (cardId: number) => {
    const { qrCache } = get();
    const newCache = new Map(qrCache);
    newCache.delete(cardId);
    set({ qrCache: newCache });
  },

  /**
   * Clear all QR codes from cache
   */
  clearAllQR: () => {
    set({ qrCache: new Map() });
  },

  /**
   * Set error message manually
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set({ qrCache: new Map(), loading: false, error: null });
  },
}));
